/**
 * RenewalCalendar — one month of upcoming overhead renewals (insurance,
 * subscriptions, plans…) laid out on a calendar with the amount due per day
 * and the month's total. Data: GET /overhead/renewals?month=YYYY-MM.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const CAT_DOT = { vehicles: "bg-sky-400", insurance: "bg-amber-400", app: "bg-violet-400", cellular: "bg-emerald-400", "407": "bg-orange-400", bonuses: "bg-pink-400", subscriptions: "bg-indigo-400", other: "bg-white/50" };

export default function RenewalCalendar() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const month = ym(cursor);

  useEffect(() => {
    setData(null); setPicked(null);
    api.get("/overhead/renewals", { params: { month } }).then((r) => setData(r.data)).catch(() => setData({ days: [], total: 0, count: 0 }));
  }, [month]);

  const first = cursor.getDay();                                  // 0 = Sunday
  const nDays = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const byDay = Object.fromEntries((data?.days || []).map((d) => [d.date, d]));
  const today = new Date().toISOString().slice(0, 10);
  const cells = [...Array(first).fill(null), ...Array.from({ length: nDays }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  while (cells.length % 7) cells.push(null);
  const sel = picked ? byDay[picked] : null;

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="renewal-calendar">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} aria-label="Previous month" data-testid="renewal-prev"
            className="rounded-md border border-white/10 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[9rem] text-center font-mono text-sm font-bold uppercase tracking-wider text-white" data-testid="renewal-month">
            {cursor.toLocaleDateString("en-CA", { month: "long", year: "numeric" })}
          </span>
          <button type="button" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Next month" data-testid="renewal-next"
            className="rounded-md border border-white/10 p-1.5 text-white/70 hover:border-[#1a3fd1] hover:text-white"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/60" data-testid="renewal-total">
          {data ? <>{data.count} renewal{data.count === 1 ? "" : "s"} · <span className="font-bold text-amber-300">{fmtMoney(data.total)}</span> due this month</> : <Loader2 className="inline h-3 w-3 animate-spin" />}
        </p>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="px-1 py-0.5 text-center font-mono text-[9px] uppercase tracking-widest text-white/40">{d}</div>)}
        {cells.map((date, i) => {
          if (!date) return <div key={`e${i}`} className="min-h-[64px] rounded-md border border-transparent" />;
          const day = byDay[date];
          const isToday = date === today;
          return (
            <button key={date} type="button" onClick={() => day && setPicked(picked === date ? null : date)} data-testid={`renewal-day-${date}`}
              className={`min-h-[64px] rounded-md border p-1 text-left transition-colors ${day ? "border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20" : "border-white/5 bg-white/[0.01]"} ${picked === date ? "ring-1 ring-[#1a3fd1]" : ""} ${isToday ? "border-[#1a3fd1]" : ""}`}>
              <span className={`font-mono text-[10px] ${isToday ? "font-bold text-[#8fa8ff]" : "text-white/50"}`}>{Number(date.slice(-2))}</span>
              {day && (
                <>
                  <span className="block truncate font-mono text-[11px] font-bold tabular-nums text-amber-200">{fmtMoney(day.total)}</span>
                  <span className="mt-0.5 flex flex-wrap gap-0.5">{day.items.slice(0, 6).map((it) => <span key={it.expense_id} className={`h-1.5 w-1.5 rounded-full ${CAT_DOT[it.category] || CAT_DOT.other}`} title={`${it.vendor} · ${fmtMoney(it.amount)}`} />)}</span>
                </>
              )}
            </button>
          );
        })}
      </div>
      {sel && (
        <div className="mt-3 rounded-md border border-white/10 bg-black/30 p-3" data-testid="renewal-day-detail">
          <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-white/50">{sel.date} · {fmtMoney(sel.total)} due</p>
          <ul className="divide-y divide-white/5">
            {sel.items.map((it) => (
              <li key={it.expense_id} className="flex items-center justify-between gap-2 py-1 text-xs">
                <span className="min-w-0"><span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${CAT_DOT[it.category] || CAT_DOT.other}`} /><span className="font-semibold text-white">{it.vendor}</span>
                  <span className="ml-1.5 font-mono text-[10px] uppercase text-white/50">{it.category}</span>{it.description && <span className="block truncate text-white/60">{it.description}</span>}</span>
                <span className="shrink-0 font-mono tabular-nums text-amber-200">{fmtMoney(it.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data && data.count === 0 && <p className="mt-2 text-center font-mono text-[10px] text-white/40" data-testid="renewal-empty">No renewals this month — set a renewal date on any bill in the table to see it here.</p>}
    </div>
  );
}
